import { useEffect, useMemo } from 'react';
import warning from 'warning';
import { AsYouType, parseDigits, getCountryCallingCode, parsePhoneNumberFromString, getCountries, isSupportedCountry } from 'libphonenumber-js/max';
import metadata from 'libphonenumber-js/metadata.min.json';

function useControlledWarning(controlled, value) {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(function () {
      var createMessage = function createMessage(preamble) {
        return preamble + " <PhoneInput /> should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled <PhoneInput /> for the lifetime of the component. Check the `value` prop being passed in.";
      };
      process.env.NODE_ENV !== "production" ? warning(!(controlled && value == null), createMessage('<PhoneInput /> is changing from controlled to uncontrolled.')) : void 0;
      process.env.NODE_ENV !== "production" ? warning(!(!controlled && value != null), createMessage('<PhoneInput /> is changing from uncontrolled to controlled.')) : void 0;
    }, [controlled, value]);
  }
}
function transformValue(value) {
  if (value === undefined) {
    return '';
  }
  var asYouType = new AsYouType(undefined);
  asYouType.input(value);
  var phoneNumber = asYouType.getNumber();
  if (phoneNumber) {
    return parseDigits(phoneNumber.formatNational());
  }
  return '';
}
function stripCountryCallingCode(value) {
  var countryCallingCode = Object.keys(metadata.country_calling_codes).find(function (callingCode) {
    return value.indexOf(callingCode) === '+'.length;
  });
  if (countryCallingCode) {
    return value.slice(("+" + countryCallingCode).length);
  }
  return '';
}
function isCountryCode(countryCode) {
  return isSupportedCountry(countryCode);
}
function migrateNationalNumber(country, value) {
  var international = value[0] === '+';
  if (international) {
    if (isCountryCode(country) && value.indexOf("+" + getCountryCallingCode(country)) === 0) {
      return stripCountryCallingCode(value);
    }
    return '';
  }
  return value;
}
function toE164(country, value) {
  if (!value) {
    return undefined;
  }
  var international = value[0] === '+';
  if (international) {
    if (value === '+') {
      return undefined;
    }
    return value;
  }
  if (isCountryCode(country)) {
    var formatter = new AsYouType(country);
    formatter.input(value);
    var phoneNumber = formatter.getNumber();
    if (phoneNumber && phoneNumber.nationalNumber) {
      return "+" + getCountryCallingCode(country) + phoneNumber.nationalNumber;
    }
  }
  return undefined;
}
function getCountryFromPartialInputValue(country, value) {
  if (!value || value === '+') {
    return country;
  }
  var asYouType = new AsYouType();
  asYouType.input(value);
  var derivedCountry = asYouType.getCountry();
  if (derivedCountry) {
    return derivedCountry;
  }
  return country;
}
function getCountryFromPartialE164(country, value) {
  var phoneNumber = parsePhoneNumberFromString(value || '');
  if (phoneNumber && phoneNumber.country) {
    return phoneNumber.country;
  }
  return country;
}
function sortAlphabetically(a, b) {
  if (a.label < b.label) {
    return -1;
  }
  if (a.label > b.label) {
    return 1;
  }
  return 0;
}
function useCountries(translations) {
  return useMemo(function () {
    return getCountries().map(function (code) {
      return {
        code: code,
        label: (translations === null || translations === void 0 ? void 0 : translations[code]) || code
      };
    }).sort(sortAlphabetically);
  }, [translations]);
}

export { getCountryFromPartialE164, getCountryFromPartialInputValue, isCountryCode, migrateNationalNumber, sortAlphabetically, stripCountryCallingCode, toE164, transformValue, useControlledWarning, useCountries };
